import { mailboxApiClient } from 'myNet'

interface Alias {
  id: string
  name: string
  email: string
}

interface GroupMail {
  name: string
  email: string
}

interface AliasesAndGroupMails {
  aliases: Alias[]
  groupmails: GroupMail[]
}

interface QuotaData {
  [key: string]: any
}

interface AutomaticRepliesData {
  [key: string]: any
}

interface RecipientsParams {
  [key: string]: any
}

interface RecipientsData {
  [key: string]: any
}

interface PasswdData {
  [key: string]: any
}

interface ForwardParams {
  [key: string]: any
}

interface ForwardData {
  [key: string]: any
}

interface AliasData {
  [key: string]: any
}

interface ACLParams {
  type?: number
}

interface ACLData {
  type: number
  subject: string
}

// interface ACLFindParams {
//   name: string
// }

interface FilterCondition {
  test: 'always' | 'from' | 'to' | 'cc' | 'subject' | 'body' | 'subjectOrBody' | 'over' | 'under'
  value: string
  option?: 'all' | 'domain'
  not?: boolean
}

interface FilterAction {
  action: 'move' | 'copy' | 'delete' | 'markRead' | 'forward' | 'redirect' | 'markJunk'
  arg?: string
}

interface FilterRule {
  conditions: FilterCondition[]
  actions: FilterAction[]
  stop?: boolean
}

// interface FilterDto {
//   id: number
//   order?: number
//   name?: string
//   rule: FilterRule
//   enabled?: boolean
//   created_at?: string
//   updated_at?: string
// }

interface CreateFilterData {
  rule: FilterRule
  order?: number
  name?: string
  enabled?: boolean
}

interface UpdateFilterData {
  rule?: FilterRule
  order?: number
  name?: string
  enabled?: boolean
}

export type InviteeCheckReason = 'ok' | 'disabled' | 'no_scheduling_inbox'

export interface InviteeCheckResponse {
  email: string
  invitable: boolean
  reason: InviteeCheckReason
}

const mailboxApiHelper = {
  getAliasesAndGroupMails: async (userDomain: string): Promise<AliasesAndGroupMails> => {
    const res = await mailboxApiClient.get('/member?imapinfo=false')
    const aliases = res.data._aliases.map((alias: any) => {
      const email = `${alias.local_part}@${userDomain}`
      return {
        id: alias.id,
        name: alias.name,
        email,
      }
    })

    const groupmails = res.data._maillists.map((maillist: any) => {
      const email = `${maillist.local_part}@${userDomain}`
      return {
        name: maillist.name,
        email,
      }
    })

    return { aliases, groupmails }
  },
  getQuota: async (): Promise<QuotaData> => {
    const res = await mailboxApiClient.get('/member?imapinfo=true')
    return res.data
  },
  getAutomaticReplies: async (): Promise<AutomaticRepliesData> => {
    const res = await mailboxApiClient.get('/member/vacation')
    return res.data
  },
  createAutomaticReplies: async (data: AutomaticRepliesData): Promise<AutomaticRepliesData> => {
    const res = await mailboxApiClient.post('/member/vacation', data)
    return res.data
  },
  updateAutomaticReplies: async (data: AutomaticRepliesData): Promise<AutomaticRepliesData> => {
    const res = await mailboxApiClient.put('/member/vacation', data)
    return res.data
  },
  deleteAutomaticReplies: async (): Promise<any> => {
    const res = await mailboxApiClient.delete('/member/vacation')
    return res.data
  },
  getRecipients: async (params: RecipientsParams): Promise<RecipientsData> => {
    const res = await mailboxApiClient.get('/member/recipients', params)
    return res.data
  },
  putPasswd: async (data: PasswdData): Promise<any> => {
    const res = await mailboxApiClient.put('/member/passwd', data)
    return res
  },
  getForward: async (params: ForwardParams): Promise<any> => {
    const res = await mailboxApiClient.get('/member/forward', params)
    return res
  },
  postForward: async (data: ForwardData): Promise<any> => {
    const res = await mailboxApiClient.post('/member/forward', data)
    return res
  },
  putForward: async (data: ForwardData): Promise<any> => {
    const res = await mailboxApiClient.put('/member/forward', data)
    return res
  },
  deleteForward: async (data: ForwardData): Promise<any> => {
    const res = await mailboxApiClient.delete('/member/forward', data)
    return res
  },
  postAlias: async (data: AliasData): Promise<any> => {
    const res = await mailboxApiClient.post('/member/aliases', data)
    return res
  },
  putAlias: async (id: string, data: AliasData): Promise<any> => {
    const res = await mailboxApiClient.put(`/member/aliases/${id}`, data)
    return res
  },
  deleteAlias: async (id: string): Promise<any> => {
    const res = await mailboxApiClient.delete(`/member/aliases/${id}`)
    return res
  },
  // 초대 대상의 초대 가능 여부 조회. 도메인은 서버가 JWT 토큰에서 가져오므로 local part 만 보낸다.
  checkInvitee: async (localPart: string) => {
    return await mailboxApiClient.get<InviteeCheckResponse>('/member/check_invitee', {
      local_part: localPart,
    })
  },
  ACL: {
    find: async (name: string): Promise<any> => {
      const res = await mailboxApiClient.get('/member/acl/find', { name })
      return res
    },
    // params: { type: 0 | 1 | 2 } 1 - blackList, 2 - whiteList
    get: async (params: ACLParams): Promise<any> => {
      const res = await mailboxApiClient.get('/member/acl', params)
      return res
    },
    // data: { type: 0 | 1 | 2, subject: string }
    post: async (data: ACLData): Promise<any> => {
      const res = await mailboxApiClient.post('/member/acl', data)
      return res
    },
    // data: { type: 0 | 1 | 2, subject: string }
    put: async (id: string, data: ACLData): Promise<any> => {
      const res = await mailboxApiClient.put(`/member/acl/${id}`, data)
      return res
    },
    delete: async (id: string): Promise<any> => {
      const res = await mailboxApiClient.delete(`/member/acl/${id}`)
      return res
    },
  },
  filter: {
    getAll: async (): Promise<any> => {
      const res = await mailboxApiClient.get('/member/filter')
      return res
    },
    get: async (id: number): Promise<any> => {
      const res = await mailboxApiClient.get(`/member/filter/${id}`)
      return res
    },
    post: async (data: CreateFilterData): Promise<any> => {
      const res = await mailboxApiClient.post('/member/filter', data)
      return res
    },
    put: async (id: number, data: UpdateFilterData): Promise<any> => {
      const res = await mailboxApiClient.put(`/member/filter/${id}`, data)
      return res
    },
    delete: async (id: number): Promise<any> => {
      const res = await mailboxApiClient.delete(`/member/filter/${id}`)
      return res
    },
    reorder: async (ids: number[]): Promise<any> => {
      const res = await mailboxApiClient.put('/member/filter/reorder', ids)
      return res
    },
  },
}

export default mailboxApiHelper
